import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { isInitialRenderWithResultsAtom } from '@/pages/SearchPageSimplified/atoms/searchPageAtoms';
import { useAppSelector } from '@/redux/hooks';
import { getSearchUiState } from '@/redux/modules/search/search.selectors';
import { getSecondarySearchSuggestions } from '@/redux/modules/searchSuggestions';
import { useTrackAuctionHouseSuggestionClick } from '@/utils/amplitude/analytics';
import { useAuctionHouseSearchFilteringExperiment } from '@/utils/amplitude/experiments/hooks/useAuctionHouseSearchFiltering';
import { safelyEscapeText } from '@/utils/safelyRender';
import { getSellerUrl } from '@/utils/urls';

type Props = {
    isLoadingSearchResults: boolean;
};

export const AuctionHouseSuggestionLink = ({ isLoadingSearchResults }: Props) => {
    const isInitialRenderWithResults = useAtomValue(isInitialRenderWithResultsAtom);

    const { houses } = useAppSelector(getSecondarySearchSuggestions);

    const trackAuctionHouseSuggestionClick = useTrackAuctionHouseSuggestionClick();

    // Experiment: sear-701-auction-house-search-filtering
    const hasAuctionHouseSearchFilteringExperiment = useAuctionHouseSearchFilteringExperiment();

    const searchSliceState = useAppSelector(getSearchUiState);
    const filterApplied = searchSliceState.queryModifications?.queryRewrite?.filterApplied;
    const shouldHideAuctionHouseSuggestionLink =
        hasAuctionHouseSearchFilteringExperiment &&
        filterApplied?.field === 'AuctionHouse' &&
        filterApplied?.values?.length === 1;

    const firstHouse = useMemo(() => {
        if (houses.length > 0) {
            return houses[0];
        }
        return null;
    }, [houses]);

    if ((!firstHouse && !isInitialRenderWithResults) || shouldHideAuctionHouseSuggestionLink) {
        return null;
    }

    return (
        <div
            className={cn('mb-sm', {
                hidden: isInitialRenderWithResults || (!firstHouse && isLoadingSearchResults),
            })}
            data-testid="auctionHouseSuggestionLinkContainer"
        >
            <BodyPrimary>
                <FormattedMessage
                    id="searchResults.lookingFor"
                    values={{
                        suggestion: firstHouse ? (
                            <Link
                                aria-label={firstHouse.value}
                                href={getSellerUrl(firstHouse.houseId, firstHouse.value)}
                                key="house-link"
                                onClick={() => trackAuctionHouseSuggestionClick({ houseId: firstHouse.houseId })}
                            >
                                {safelyEscapeText(firstHouse.value).trim()}
                            </Link>
                        ) : (
                            <span key="house-suggestion" />
                        ),
                    }}
                />
            </BodyPrimary>
        </div>
    );
};
